import { memo, useCallback } from 'react';

import { type BaseCustomField } from '@air/api/types';

import { useToggleViewVisibilityMenuOption } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useToggleViewVisibilityMenuOption';
import { useToggleViewVisibilityMenuOptions } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useToggleViewVisibilityMenuOptions';
import { ViewVisibilitySection } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewVisibilitySection/ViewVisibilitySection';
import { ViewVisibilityToggle } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewVisibilitySection/ViewVisibilityToggle';
import { customFieldIconMap } from '~/constants/customFieldIconMap';
import { type ConfigViewOption } from '~/store/configViews/types';

export type CustomFieldsVisbilityOptionsProps = {
  options: ConfigViewOption[];
  customFields: BaseCustomField[];
};

export const CustomFieldsVisbilityOptions = memo(({ options, customFields }: CustomFieldsVisbilityOptionsProps) => {
  const { toggleViewVisibilityMenuOption } = useToggleViewVisibilityMenuOption();
  const { toggleViewVisibilityMenuOptions } = useToggleViewVisibilityMenuOptions();
  const initialShowOptions = options.some(({ isVisible }) => isVisible);

  const handleToggleShowingOptions = useCallback(
    (showing: boolean) => {
      toggleViewVisibilityMenuOptions(options, showing);
    },
    [options, toggleViewVisibilityMenuOptions],
  );

  return (
    <ViewVisibilitySection
      initialShowOptions={initialShowOptions}
      id="customFields"
      label="Custom fields"
      onToggleShowingOptions={handleToggleShowingOptions}
      toggles={options.map((option) => {
        const customField = customFields.find((cf) => cf.id === option.customFieldId);
        const customFieldTypeName = customField?.type.title;
        const Icon = customFieldTypeName ? customFieldIconMap[customFieldTypeName] : null;

        return (
          <ViewVisibilityToggle
            key={option.fieldName}
            onToggleOption={toggleViewVisibilityMenuOption}
            option={option}
            prefix={!!Icon && <Icon width={16} />}
          />
        );
      })}
    />
  );
});

CustomFieldsVisbilityOptions.displayName = 'CustomFieldsVisbilityOptions';
